import React from 'react'

import { cn } from '@/lib/utils'

export interface InputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {}

function isNumericInput(
  type?: string,
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode']
) {
  return type === 'number' || inputMode === 'decimal' || inputMode === 'numeric'
}

function stripLeadingZeros(raw: string): string {
  if (raw === '' || raw === '-' || raw === '.' || raw === '-.') return raw
  if (raw === '0' || raw === '-0' || raw.startsWith('0.') || raw.startsWith('-0.')) return raw
  if (/^-?0+\d/.test(raw)) {
    const n = Number(raw)
    if (Number.isFinite(n)) return String(n)
  }
  return raw
}

function isEmptyNumeric(raw: string): boolean {
  const t = raw.trim()
  return t === '' || t === '-' || t === '.' || t === '-.'
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, inputMode, min, onChange, onFocus, onBlur, readOnly, disabled, ...props }, ref) => {
    const numeric = isNumericInput(type, inputMode)
    const restoreZeroOnEmpty = numeric && !(Number(min) > 0)

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      if (numeric && !readOnly && !disabled) {
        const n = Number(e.target.value)
        if (e.target.value === '' || n === 0) {
          e.target.select()
        }
      }
      onFocus?.(e)
    }

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (numeric && !readOnly && !disabled) {
        const next = stripLeadingZeros(e.target.value)
        if (next !== e.target.value) {
          e.target.value = next
        }
      }
      onChange?.(e)
    }

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      if (restoreZeroOnEmpty && !readOnly && !disabled && isEmptyNumeric(e.target.value)) {
        e.target.value = '0'
        onChange?.({
          ...e,
          type: 'change',
          target: e.target,
          currentTarget: e.currentTarget,
        } as React.ChangeEvent<HTMLInputElement>)
      }
      onBlur?.(e)
    }

    return (
      <input
        {...props}
        type={type}
        inputMode={inputMode}
        min={min}
        readOnly={readOnly}
        disabled={disabled}
        className={cn(
          'flex h-9 w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
          className
        )}
        ref={ref}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
      />
    )
  }
)
Input.displayName = 'Input'

export { Input }
